import { Handle, NodeToolbar, Position } from "@xyflow/react";
import BoxesIcon from "lucide-react/dist/esm/icons/boxes";
import PlusIcon from "lucide-react/dist/esm/icons/plus";
import TrashIcon from "lucide-react/dist/esm/icons/trash";
import PencilIcon from "lucide-react/dist/esm/icons/pencil";
import EyeIcon from "lucide-react/dist/esm/icons/eye";
import { Button } from "@quake/shared-ui/components/ui/button";
import { cn } from "@quake/shared-ui/lib/utils";
import { useWorkflowBuilderStore } from "../store/useWorkflowBuilderStore";
import { NodeData } from "../types";
import { useState } from "react";

export type EntityBatchNodeProps = {
  id: string;
  data: NodeData;
  fallbackLabel: string;
  onAddAction?: ({ branch }: { branch?: "truthy" | "falsy" }) => void;
  onDelete?: () => void;
  onEdit?: () => void;
  readOnly?: boolean;
  onAddChildAction?: () => void;
  allowActionPreview?: boolean;
};

export function EntityBatchNode({
  id,
  data,
  fallbackLabel,
  onAddAction,
  onDelete,
  onEdit,
  readOnly = false,
  onAddChildAction,
  allowActionPreview = true,
}: EntityBatchNodeProps) {
  const direction = useWorkflowBuilderStore((state) => state.meta?.direction);
  const [isDragOver, setIsDragOver] = useState(false);
  const input = data.action?.parameters?.[0]?.input ?? {};
  const childCount = data.childNodes?.length || 0;
  const settings = [
    input.batchSize ? `Batch: ${input.batchSize}` : null,
    input.maxBatches ? `Max: ${input.maxBatches}` : null,
  ].filter(Boolean);

  return (
    <div
      data-node-id={id}
      className="relative"
      onDragOver={(event) => {
        event.preventDefault();
        setIsDragOver(true);
      }}
      onDragLeave={() => setIsDragOver(false)}
      onDrop={() => setIsDragOver(false)}
    >
      {allowActionPreview ? (
        <NodeToolbar isVisible position={Position.Top} className="flex gap-2">
          <Button variant="ghost" size="icon" className="h-8 w-8 bg-white shadow-sm dark:bg-slate-800" onClick={onEdit}>
            {readOnly ? <EyeIcon className="h-4 w-4" /> : <PencilIcon className="h-4 w-4" />}
          </Button>
          {!readOnly && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 bg-white shadow-sm dark:bg-slate-800"
              onClick={onDelete}
            >
              <TrashIcon className="h-4 w-4 text-red-600" />
            </Button>
          )}
        </NodeToolbar>
      ) : null}

      <div
        className={cn(
          "rounded-lg border-2 border-dashed bg-slate-50/70 dark:bg-slate-900/50",
          "border-emerald-500 dark:border-emerald-500 min-w-100 min-h-75 transition-all duration-200",
          isDragOver && "border-emerald-600 bg-emerald-50/70 dark:bg-slate-800",
          !readOnly && childCount === 0 && "cursor-pointer hover:border-emerald-600",
        )}
        style={{ padding: "50px" }}
        onClick={(event) => {
          if (!readOnly && childCount === 0 && onAddChildAction) {
            event.stopPropagation();
            onAddChildAction();
          }
        }}
      >
        <div className="absolute top-2 left-2 right-2 flex items-center justify-between gap-2 pointer-events-none">
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-emerald-600 text-white shadow-sm">
            <BoxesIcon className="h-4 w-4" />
            <span className="font-medium text-sm">{data.label || fallbackLabel}</span>
            {childCount > 0 && (
              <span className="ml-1 px-1.5 py-0.5 bg-white/20 rounded text-xs">
                {childCount} {childCount === 1 ? "node" : "nodes"}
              </span>
            )}
          </div>
          {settings.length > 0 && (
            <div className="text-xs text-muted-foreground bg-background/90 px-2 py-1 rounded shadow-sm">
              {settings.join(" • ")}
            </div>
          )}
        </div>

        {childCount === 0 && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="text-center text-muted-foreground">
              <BoxesIcon className="h-12 w-12 mx-auto mb-2 opacity-30" />
              <p className="text-sm">Drag batch actions here or use + button</p>
            </div>
          </div>
        )}

        <Handle
          id="target"
          type="target"
          position={direction === "top-to-bottom" ? Position.Top : Position.Left}
          className="bg-emerald-600! w-3! h-3!"
        />
        <Handle
          id="source"
          type="source"
          position={direction === "top-to-bottom" ? Position.Bottom : Position.Right}
          className="bg-emerald-600! w-3! h-3!"
        />
      </div>

      {data.note && <div className="automation-node-note">{data.note}</div>}

      {!readOnly && (
        <NodeToolbar isVisible position={direction === "top-to-bottom" ? Position.Bottom : Position.Right}>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 bg-white shadow-sm dark:bg-slate-800"
            onClick={() => onAddAction?.({})}
          >
            <PlusIcon className="h-4 w-4" />
          </Button>
        </NodeToolbar>
      )}
    </div>
  );
}
